import useRouter from "@stadium/hooks/lib/use-custom-route";
import { useCallback, useContext, useEffect, useMemo, useState, createContext } from "react";
import { useSelector } from "react-redux";
import { showSuccessToast } from "@stadium/services/react-toastify";
import { useAuth0V2 } from "@stadium/services/auth0";

import { convertPrice } from "@stadium/helpers/currency";

import useFetchSavedCreditCardsAndWalletInfo from "@stadium/checkout/src/hooks/use-fetch-saved-cards-and-team-wallet-info";
import { useCheckoutContext as useGlobalCheckoutContext } from "@stadium/checkout/src/contexts/checkout";

import { getConversionRates, getPompFeeInUsd } from "@stadium/redux-utils/selectors/configSelector";
import {
  getCartOrderAuthorizedPayments,
  getCartOrderLineItems,
  getCartOrderMedleySets,
  getCartOrderPlatformFee,
  getCartOrderTaxRecoveryFee,
  getCartOrderProductSets,
  getCartTaxTotal,
  getIsCartOrderUpdated,
  getCartPromoTotal,
  getCartItemsCount,
  getCartOrderCurrency,
  getPompAddOnEnabled,
  getCartOrderBagTotal,
} from "@selectors/cart";
import { getIsPricelessShop } from "@selectors/store";

import { useConfig } from "@stadium/hooks/lib/use-config";
import useStoreCurrencyConverter from "@hooks/currency/use-store-currency-converter";

import { CurrentUserContext } from "@contexts/current-user-provider";
import { useStoreContext } from "@components/store/provider";

import { DEFAULT_CURRENCY_CODE } from "@stadium/constants";
import { getCartRoute } from "@constants/routes";
import { CART_UPDATED_TOAST_ID } from "@constants/toast-ids";

const CheckoutContext = createContext();

const PRICELESS_SHOP_CHECKOUT_TOAST_MESSAGE =
  "The availability of some of the products in your cart have been updated.";
const NORMAL_SHOP_CHECKOUT_TOAST_MESSAGE =
  "The price and availability of some of the products in your cart have been updated.";

export const useCheckoutContext = () => useContext(CheckoutContext);

const emptyObj = {};

const CheckoutProvider = ({
  children,
  canShowCartUpdatedMessage = false,
  skipPaymentOptions = false,
}) => {
  const { isAuthenticated } = useAuth0V2();
  const lineItems = useSelector(getCartOrderLineItems);
  const productSets = useSelector(getCartOrderProductSets);
  const medleySets = useSelector(getCartOrderMedleySets);
  const { push } = useRouter();
  const { storeBasePath, storeDisplayCurrencyCode, storeCurrencyCode } = useStoreContext();
  const { convertPriceInDisplayStoreCurrency, formatPriceInDisplayStoreCurrency } =
    useStoreCurrencyConverter();
  const orderCurrencyCode = useSelector(getCartOrderCurrency);

  const convertPriceInDisplayCurrency = (price) => {
    return convertPriceInDisplayStoreCurrency(price, orderCurrencyCode);
  };
  const conversionRates = useConfig(getConversionRates);
  const configPompFee = useConfig(getPompFeeInUsd);

  const { currency } = useGlobalCheckoutContext();

  const platformFee = convertPriceInDisplayCurrency(useSelector(getCartOrderPlatformFee));
  const taxRecoveryFee = convertPriceInDisplayCurrency(useSelector(getCartOrderTaxRecoveryFee));
  const promoTotal = convertPriceInDisplayCurrency(useSelector(getCartPromoTotal));
  const taxTotal = convertPriceInDisplayCurrency(useSelector(getCartTaxTotal));
  const authorizedPayments = useSelector(getCartOrderAuthorizedPayments);
  const cartItemsTotal = convertPriceInDisplayCurrency(useSelector(getCartOrderBagTotal));
  const isPricelessShop = useSelector(getIsPricelessShop);

  const isCartUpdated = useSelector(getIsCartOrderUpdated);
  const cartItemCount = useSelector(getCartItemsCount);
  const hasPomp = useSelector(getPompAddOnEnabled);

  const { currentUserData } = useContext(CurrentUserContext);

  const [submittedAddress, setSubmittedAddress] = useState(emptyObj);

  const [youPayAmount, setYouPayAmount] = useState(null);

  const [isRequestMockupConfirmationModalOpen, setIsRequestMockupConfirmationModalOpen] =
    useState(false);

  const [isAnyItemWithoutDigitalAsset, setIsAnyItemWithoutDigitalAsset] = useState(false);
  const openRequestMockupConfirmationModal = () => setIsRequestMockupConfirmationModalOpen(true);
  const closeRequestMockupConfirmationModal = () => setIsRequestMockupConfirmationModalOpen(false);

  const {
    savedCreditCards,
    teamWallets,
    refetch: refetchCreditCards,
    loading,
  } = useFetchSavedCreditCardsAndWalletInfo({ skip: !isAuthenticated || skipPaymentOptions });

  useEffect(() => {
    if (isCartUpdated && canShowCartUpdatedMessage) {
      showSuccessToast({
        message: isPricelessShop
          ? PRICELESS_SHOP_CHECKOUT_TOAST_MESSAGE
          : NORMAL_SHOP_CHECKOUT_TOAST_MESSAGE,
        config: { toastId: CART_UPDATED_TOAST_ID },
      });
      cartItemCount === 0 && push(getCartRoute({ storeBasePath }));
    }
  }, [isCartUpdated, canShowCartUpdatedMessage, isPricelessShop]);

  const getPompFeeInStoreCurrency = (convertPrice, fee, currencyCode) =>
    convertPrice ? convertPriceInDisplayStoreCurrency(fee, currencyCode) : 0;

  const pompData = useMemo(
    () => ({
      showPomp: hasPomp,
      pompFeeToDisplay: formatPriceInDisplayStoreCurrency(configPompFee, DEFAULT_CURRENCY_CODE),
      pompFeeInStoreCurrency: getPompFeeInStoreCurrency(
        hasPomp && !!configPompFee,
        configPompFee,
        DEFAULT_CURRENCY_CODE,
      ),
    }),
    [configPompFee, hasPomp],
  );

  const cartTotal = useMemo(() => {
    const { pompFeeInStoreCurrency } = pompData;
    return (
      cartItemsTotal + platformFee + taxRecoveryFee + promoTotal + taxTotal + pompFeeInStoreCurrency
    );
  }, [cartItemsTotal, platformFee, taxRecoveryFee, promoTotal, taxTotal, pompData]);

  const cartTotalExcludingTaxes = useMemo(() => {
    const { pompFeeInStoreCurrency } = pompData;
    return cartItemsTotal + promoTotal + pompFeeInStoreCurrency;
  }, [cartItemsTotal, promoTotal, pompData]);

  const getCartTotalInSelectedCurrency = useCallback(
    ({ totalToConvert }) =>
      storeCurrencyCode
        ? convertPrice({
            from: storeCurrencyCode,
            to: currency?.code,
            value: totalToConvert,
            conversionRates,
          })
        : totalToConvert,
    [storeCurrencyCode, currency?.code],
  );

  const cartItemsQuantity = useMemo(() => {
    return [...lineItems, ...productSets, ...medleySets].reduce(
      (acc, product) => acc + product.quantity,
      0,
    );
  }, [lineItems, productSets, medleySets]);

  const stadiumWalletBalance = currentUserData?.walletBalance;
  const walletCurrencyId = +currentUserData?.walletCurrency?.id;
  const walletCurrencyCode = currentUserData?.walletCurrency?.code?.toLowerCase();
  const storeCurrencyConversionRate = conversionRates[storeDisplayCurrencyCode?.toLowerCase()];
  const walletConversionRate = conversionRates[walletCurrencyCode];

  const walletDetails = {
    stadiumWalletBalance,
    walletCurrencyId,
    walletCurrencyCode,
    walletConversionRate,
  };

  const contextValues = useMemo(
    () => ({
      loading,
      orderCurrencyCode,
      submittedAddress,
      setSubmittedAddress,
      youPayAmount,
      setYouPayAmount,
      cartItemsTotal,
      walletDetails,
      storeCurrencyConversionRate,
      cartTotal,
      authorizedPayments,
      taxRecoveryFee,
      platformFee,
      promoTotal,
      taxTotal,
      cartItemsQuantity,
      savedCreditCards,
      teamWallets,
      refetchCreditCards,
      pompData,
      getCartTotalInSelectedCurrency,
      isRequestMockupConfirmationModalOpen,
      openRequestMockupConfirmationModal,
      closeRequestMockupConfirmationModal,
      isAnyItemWithoutDigitalAsset,
      setIsAnyItemWithoutDigitalAsset,
      cartTotalExcludingTaxes,
    }),
    [
      loading,
      orderCurrencyCode,
      submittedAddress,
      setSubmittedAddress,
      youPayAmount,
      setYouPayAmount,
      cartItemsTotal,
      walletDetails,
      storeCurrencyConversionRate,
      cartTotal,
      authorizedPayments,
      taxRecoveryFee,
      platformFee,
      promoTotal,
      taxTotal,
      cartItemsQuantity,
      savedCreditCards,
      teamWallets,
      refetchCreditCards,
      pompData,
      getCartTotalInSelectedCurrency,
      isRequestMockupConfirmationModalOpen,
      openRequestMockupConfirmationModal,
      closeRequestMockupConfirmationModal,
      isAnyItemWithoutDigitalAsset,
      setIsAnyItemWithoutDigitalAsset,
      cartTotalExcludingTaxes,
    ],
  );

  return <CheckoutContext.Provider value={contextValues}>{children}</CheckoutContext.Provider>;
};

export default CheckoutProvider;
